/**
 * The UTM Tracking Parameters the Form carries into HubSpot.
 *
 * Adding a name here is only one of the five touchpoints a Tracking Parameter
 * crosses (ADR-0002) — the Avada capture script, the WordPress shortcode's
 * $query_args, and a matching HubSpot contact property are all still required,
 * or the value silently arrives (or lands) empty. `server.js` keeps its own copy
 * of this list; both must change together.
 */
export const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
];

/**
 * Reads every UTM Tracking Parameter, sessionStorage first then the URL.
 *
 * sessionStorage wins because the Avada script saves the value from the
 * visitor's original landing page, which survives navigation that the Form's
 * own URL does not.
 *
 * @param {string} [url] Defaults to the Form's current URL.
 * @param {Storage} [storage] Defaults to sessionStorage; injectable for tests.
 * @returns {Object<string, string>} snake_case UTM names to values, '' when absent.
 */
export const readUtmParams = (url = window.location.href, storage) => {
    // URLSearchParams handles the percent-decoding: the shortcode builds the
    // iframe src with PHP_QUERY_RFC3986, so a utm_campaign like
    // "[March 2025] NJ events" arrives encoded and must be decoded before it
    // reaches HubSpot.
    const params = new URLSearchParams(url.split('#')[0].split('?')[1] || '');

    return UTM_PARAMS.reduce((values, name) => {
        values[name] = readStoredParam(storage, name) || params.get(name) || '';
        return values;
    }, {});
};

const readStoredParam = (storage, name) => {
    try {
        return (storage || window.sessionStorage).getItem(name);
    } catch (error) {
        // Reading storage throws when a browser blocks it for a cross-origin
        // iframe (Safari with cookies disabled). The URL params still work, so
        // fall back to those instead of dropping every value.
        return null;
    }
};
